import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  CalendarClock,
  MapPin,
  Radio,
  PlayCircle,
  Trophy,
  Users,
  Lock,
  CheckCircle2,
  X,
} from 'lucide-react';
import { fetchEventBySlug, fetchEventWatch, friendlyError, buildAssetUrl, checkEventAccess } from '@/lib/api';
import { toast } from 'sonner';
import { ROUTES } from '@/lib/constants';
import { EVENTS as TID } from '@/constants/testIds';
import {
  EventStatusBadge,
  EventAccessBadge,
  formatEventDate,
} from '@/components/events/EventStatusBadge';
import { EventVideoEmbed } from '@/components/events/EventVideoEmbed';
import { EventPaywall } from '@/components/events/EventPaywall';
import { EventDownloadBlock } from '@/components/events/EventDownloadBlock';
import { EventContentsSection } from '@/components/events/EventContentsSection';
import { useAuth } from '@/contexts/AuthContext';

function VideoPlaceholderBlock({ status, accessType }) {
  // Imagen de fondo común (estadio iluminado · retransmisión)
  const liveBgUrl = "https://images.unsplash.com/photo-1522778526097-ce0a22ceb253?auto=format&fit=crop&w=1280&q=70";

  if (status === 'live') {
    return (
      <div
        className="relative rounded-2xl overflow-hidden border border-[var(--ns-border)]"
        data-testid={TID.detailLivePlaceholder}
      >
        <div className="aspect-video w-full bg-cover bg-center"
             style={{ backgroundImage: `url('${liveBgUrl}')` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ns-red)]/85 via-black/55 to-black/30" aria-hidden="true" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6 max-w-md">
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-white bg-[var(--ns-red)] px-3 py-1.5 rounded-full mb-4 animate-pulse shadow-lg">
              <Radio className="h-3.5 w-3.5" /> En directo
            </div>
            <p className="text-base sm:text-lg font-display text-white drop-shadow-md">
              El reproductor en directo estará disponible en la próxima fase.
            </p>
            <p className="mt-2 text-xs text-white/85 drop-shadow-md">
              Cuando integremos streaming, el evento se reproducirá directamente aquí.
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (status === 'replay_available') {
    return (
      <div
        className="relative rounded-2xl overflow-hidden border border-[var(--ns-border)]"
        data-testid={TID.detailReplayPlaceholder}
      >
        <div className="aspect-video w-full bg-cover bg-center"
             style={{ backgroundImage: `url('${liveBgUrl}')` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/20" aria-hidden="true" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6 max-w-md">
            <div className="h-14 w-14 mx-auto rounded-2xl bg-[var(--ns-green)]/80 text-white inline-flex items-center justify-center mb-4 shadow-lg">
              <PlayCircle className="h-7 w-7" />
            </div>
            <p className="text-base sm:text-lg font-display text-white drop-shadow-md">
              Replay disponible próximamente
            </p>
            <p className="mt-2 text-xs text-white/85 drop-shadow-md">
              {accessType === 'free'
                ? 'Podrás revivir el evento aquí cuando integremos VOD.'
                : 'El acceso al replay requerirá ' + (accessType === 'premium' ? 'plan premium' : accessType === 'subscription' ? 'suscripción activa' : 'desbloquear el contenido') + '.'}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="relative rounded-2xl overflow-hidden border border-[var(--ns-border)]"
      data-testid={TID.detailVideoPlaceholder}
    >
      <div className="aspect-video w-full bg-cover bg-center"
           style={{ backgroundImage: `url('${liveBgUrl}')` }} aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/25" aria-hidden="true" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center px-6 max-w-md">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-white bg-[var(--ns-red)]/90 px-3 py-1.5 rounded-full mb-4 shadow-lg">
            <Radio className="h-3.5 w-3.5" /> Próximo directo
          </div>
          <p className="text-base sm:text-lg font-display text-white drop-shadow-md">
            Vuelve cuando empiece el evento
          </p>
          <p className="mt-2 text-xs text-white/85 drop-shadow-md">
            Cuando el partido esté en directo o tenga replay disponible, podrás verlo desde aquí.
          </p>
        </div>
      </div>
    </div>
  );
}

function TeamCard({ team, label }) {
  return (
    <div className="flex-1 min-w-0 rounded-xl border border-[var(--ns-border)] bg-white p-4">
      <p className="text-[10px] uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold">{label}</p>
      <div className="mt-1 flex items-center gap-2">
        <div className="h-9 w-9 rounded-lg bg-[var(--ns-bg-2)] inline-flex items-center justify-center text-[var(--ns-gold-700)] shrink-0">
          <Users className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{team.name || '—'}</p>
          {team.club_name ? (
            <Link
              to={team.club_slug ? ROUTES.clubDetail(team.club_slug) : '#'}
              className="text-xs text-[var(--ns-text-muted)] hover:text-[var(--ns-gold-700)] truncate block"
            >
              {team.club_name}
              {team.category ? ` · ${team.category}` : ''}
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null); // null | {access, reason, requires}
  const [accessLoading, setAccessLoading] = useState(false);
  const [contentVideos, setContentVideos] = useState([]);
  const [contentRefreshTick, setContentRefreshTick] = useState(0);
  // ---- Confirmación de compra de descarga (query `?compra=descarga`) ----
  // Al volver desde el checkout, EventContentsSection/WatchPage redirigen
  // a /eventos/{slug}?compra=descarga#contenidos. Mostramos un banner y
  // forzamos una recarga de los contenidos para que el entitlement
  // `owned` se refleje de inmediato en el bloque de descarga.
  const [downloadPurchaseConfirmed, setDownloadPurchaseConfirmed] = useState(false);
  useEffect(() => {
    if (searchParams.get('compra') !== 'descarga') return;
    setDownloadPurchaseConfirmed(true);
    setContentRefreshTick((t) => t + 1);
    toast.success('Compra de descarga confirmada.');
    // Limpiamos el query param conservando la ruta y el hash
    // (#contenidos) para que un F5 no vuelva a disparar el estado.
    const clean = `${location.pathname}${location.hash || ''}`;
    navigate(clean, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setEvent(null);
    setAccess(null);
    (async () => {
      try {
        const res = await fetchEventBySlug(slug);
        if (!cancelled) {
          setEvent(res);
          // Comprobar acceso solo si el evento NO es free y el usuario está logueado
          const at = (res.access_type || '').toLowerCase();
          if (at && at !== 'free' && user) {
            setAccessLoading(true);
            try {
              const acc = await checkEventAccess(res.id);
              if (!cancelled) setAccess(acc);
            } catch (_e) {
              if (!cancelled) setAccess({ access: false, reason: 'check_failed', requires: 'purchase' });
            } finally {
              if (!cancelled) setAccessLoading(false);
            }
          } else if (at === 'free') {
            setAccess({ access: true, reason: 'free' });
          } else if (!user) {
            setAccess({ access: false, reason: 'unauthenticated', requires: 'purchase' });
          }
        }
      } catch (err) {
        if (!cancelled) {
          const apiErrorCode = err?.response?.data?.error;
          const isNotFound =
            apiErrorCode === 'event_not_found' || err?.response?.status === 404;
          if (isNotFound) {
            toast.error('Evento no encontrado.');
            navigate(ROUTES.events, { replace: true });
            return;
          }
          toast.error(friendlyError(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate, user]);

  // Cargar contenidos del partido (Entrega 3)
  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchEventWatch(slug);
        if (!cancelled) setContentVideos(data?.videos || []);
      } catch (_) {
        if (!cancelled) setContentVideos([]);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, user, contentRefreshTick]);

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid={TID.detailPage}>
          <Skeleton className="h-64 sm:h-80 w-full rounded-2xl" />
          <div className="mt-6 space-y-4">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-20 w-full" />
          </div>
        </div>
      </MainLayout>
    );
  }
  if (!event) return null;

  const dateLabel = formatEventDate(event.event_date);
  const locationParts = [event.venue, event.city, event.province, event.country].filter(Boolean);

  return (
    <MainLayout>
      <div data-testid={TID.detailPage} className="pb-12">
        {/* Hero */}
        <section
          className="relative w-full h-64 sm:h-80 lg:h-[26rem] overflow-hidden bg-gradient-to-br from-[var(--ns-gold)]/40 to-[var(--ns-gold-700)]/30"
          data-testid={TID.detailHero}
        >
          {event.thumbnail_url ? (
            <img
              src={buildAssetUrl(event.thumbnail_url)}
              alt={event.title}
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/25" />
          <div className="absolute inset-0">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-6 sm:pb-8">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(ROUTES.events)}
                className="self-start mt-4 sm:mt-6 text-white/90 hover:text-white hover:bg-white/15 h-8 px-2"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Volver a Partidos
              </Button>
              {/* Badges del hero eliminados (PRÓXIMO / GRATIS / PREMIUM / PPV
                  / deporte / categoría). Los datos siguen existiendo en
                  `event.status`, `event.access_type`, `event.sport_name` y
                  `event.category` y se utilizan en el resto de la página
                  (metadatos, CTA, tarjetas), pero no se renderizan en el
                  hero público de detalle de partido. Aplica a todos los
                  partidos existentes y futuros. */}
              <div className="mt-auto mb-1" />
              <h1
                className="font-display text-2xl sm:text-4xl lg:text-5xl text-white leading-tight drop-shadow-[0_4px_16px_rgba(0,0,0,0.55)] max-w-3xl"
                data-testid={TID.detailTitle}
              >
                {event.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90">
                {dateLabel ? (
                  <span className="inline-flex items-center gap-1.5" data-testid={TID.detailDate}>
                    <CalendarClock className="h-4 w-4 text-[var(--ns-gold)]" />
                    {dateLabel}
                  </span>
                ) : null}
                {locationParts.length > 0 ? (
                  <span className="inline-flex items-center gap-1.5" data-testid={TID.detailLocation}>
                    <MapPin className="h-4 w-4 text-[var(--ns-gold)]" />
                    {locationParts.join(' · ')}
                  </span>
                ) : null}
              </div>
              <div className="mt-4">
                <Link to={`/eventos/${event.slug}/watch`}>
                  <Button
                    data-testid="event-watch-cta"
                    className="h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0 hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white transition-all"
                  >
                    Ver contenidos
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Columna principal */}
            <div className="lg:col-span-2 space-y-8">
              {/* Paywall si el evento es premium/ppv y el usuario no tiene acceso */}
              {access && access.access === false ? (
                <EventPaywall event={event} requires={access.requires || 'purchase'} />
              ) : null}

              {/* Vídeo completo (Premium) — solo si tiene acceso o evento free */}
              {/* Preview público: ahora se integra DENTRO de la tarjeta
                  "Partido completo" (ver EventContentsSection). Aquí se
                  elimina su presentación duplicada como "Tráiler · Avance".
                  La fuente del partido completo nunca se expone en el
                  detalle; el reproductor la obtiene de
                  /eventos/{slug}/watch → /api/videos/{id}/playback. */}

              {/* Fase 05B — Bloque de descarga (si existe un download_product para este evento) */}
              <EventDownloadBlock event={event} />

              {/* Entrega 3 — Contenidos del partido (cards con CTAs de acceso/descarga) */}
              {downloadPurchaseConfirmed ? (
                <div
                  className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/80 p-3 mb-3"
                  role="status"
                  data-testid="event-detail-download-purchase-confirmed"
                >
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0 text-sm text-emerald-900">
                    <p className="font-semibold">Compra de descarga confirmada.</p>
                    <p className="text-xs text-emerald-800/80 mt-0.5">
                      Ya puedes descargar el contenido desde la ficha del partido.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDownloadPurchaseConfirmed(false)}
                    className="shrink-0 p-1 rounded-md text-emerald-700 hover:bg-emerald-100 transition-colors"
                    aria-label="Cerrar aviso"
                    data-testid="event-detail-download-purchase-confirmed-dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
              <EventContentsSection
                eventSlug={event.slug}
                eventId={event.id}
                previewVideoUrl={event.preview_video_url}
                posterUrl={event.thumbnail_url}
                videos={contentVideos}
                refresh={() => setContentRefreshTick((t) => t + 1)}
              />

              {/* Placeholder de directo/replay/próximo (solo si no hay vídeo completo y tiene acceso).
                  Va DESPUÉS de los contenidos del partido para priorizar lo que ya está disponible. */}
              {!event.video_url && (!access || access.access === true) ? (
                <VideoPlaceholderBlock status={event.status} accessType={event.access_type} />
              ) : null}

              {/* Equipos */}
              {(event.team_home || event.team_away) ? (
                <div data-testid={TID.detailTeamsBlock}>
                  <h2 className="font-display text-xl text-[var(--ns-text)] mb-3">Equipos</h2>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {event.team_home ? (
                      <TeamCard team={event.team_home} label="Local" />
                    ) : null}
                    {event.team_away ? (
                      <TeamCard team={event.team_away} label="Visitante" />
                    ) : null}
                  </div>
                </div>
              ) : null}

              {/* Descripción */}
              {event.description ? (
                <article data-testid={TID.detailDescription}>
                  <h2 className="font-display text-xl text-[var(--ns-text)]">Sobre el evento</h2>
                  <p className="mt-3 text-[var(--ns-text-muted)] leading-relaxed">
                    {event.description}
                  </p>
                </article>
              ) : null}
            </div>

            {/* Sidebar */}
            <aside className="space-y-4">
              <div className="rounded-2xl border border-[var(--ns-border)] bg-white p-5">
                <h3 className="font-display text-base text-[var(--ns-text)] mb-3">Detalles</h3>
                <dl className="space-y-3 text-sm">
                  {dateLabel ? (
                    <div className="flex items-start gap-3">
                      <CalendarClock className="h-4 w-4 text-[var(--ns-gold-600)] shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <dt className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold">Fecha</dt>
                        <dd className="text-[var(--ns-text)]">{dateLabel}</dd>
                      </div>
                    </div>
                  ) : null}
                  {event.venue ? (
                    <div className="flex items-start gap-3">
                      <Trophy className="h-4 w-4 text-[var(--ns-gold-600)] shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <dt className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold">Sede</dt>
                        <dd className="text-[var(--ns-text)]">{event.venue}</dd>
                      </div>
                    </div>
                  ) : null}
                  {locationParts.length > 0 ? (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-[var(--ns-gold-600)] shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <dt className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold">Ubicación</dt>
                        <dd className="text-[var(--ns-text)]">
                          {[event.city, event.province, event.autonomous_community_or_region, event.country].filter(Boolean).join(' · ')}
                        </dd>
                      </div>
                    </div>
                  ) : null}
                  {event.club_name ? (
                    <div className="flex items-start gap-3">
                      <Users className="h-4 w-4 text-[var(--ns-gold-600)] shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <dt className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold">Club anfitrión</dt>
                        <dd>
                          {event.club_slug ? (
                            <Link
                              to={ROUTES.clubDetail(event.club_slug)}
                              className="text-[var(--ns-text)] hover:text-[var(--ns-gold-700)] hover:underline"
                              data-testid={TID.detailClubLink}
                            >
                              {event.club_name}
                            </Link>
                          ) : (
                            <span className="text-[var(--ns-text)]">{event.club_name}</span>
                          )}
                        </dd>
                      </div>
                    </div>
                  ) : null}
                  {event.access_type && event.access_type !== 'free' ? (
                    <div className="flex items-start gap-3">
                      <Lock className="h-4 w-4 text-[var(--ns-gold-600)] shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <dt className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold">Acceso</dt>
                        <dd>
                          <EventAccessBadge accessType={event.access_type} />
                        </dd>
                      </div>
                    </div>
                  ) : null}
                </dl>
              </div>
            </aside>
          </div>
        </section>
      </div>
    </MainLayout>
  );
}
